import { versionColor } from '../colors.js';

// Canvas-rendered treemap of server storage. Uses a simple squarified fill.
export function createCacheBoxesChart(canvasEl) {
  const ctx = canvasEl.getContext('2d');
  function update(snap) {
    const rect = canvasEl.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvasEl.width = rect.width * dpr;
    canvasEl.height = rect.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Container box
    ctx.strokeStyle = '#2a313c';
    ctx.lineWidth = 1;
    ctx.strokeRect(1, 1, rect.width - 2, rect.height - 2);

    const items = [];
    for (const v of snap.versions) items.push({ kind: 'asset', assetId: v.assetId, v: v.version, size: v.sizeBytes, label: `${v.assetId}v${v.version}` });
    for (const d of snap.dictionaries) items.push({ kind: 'dict', assetId: d.sourceAssetId, v: d.sourceVersion, size: d.sizeBytes, pinned: d.pinned, label: `dict ${d.sourceAssetId}v${d.sourceVersion}` });
    for (const d of snap.deltas) items.push({ kind: 'delta', assetId: d.assetId, fromV: d.fromVersion, toV: d.toVersion, size: d.sizeBytes, label: `Δ ${d.assetId} v${d.fromVersion}→v${d.toVersion}` });

    if (!items.length) return;
    items.sort((a, b) => b.size - a.size);
    const totalActual = items.reduce((a, b) => a + b.size, 0);
    const capacity = Math.max(totalActual, (snap.cfg.server.unifiedPool ? snap.cfg.server.capacityBytes
      : (snap.cfg.server.capacityBytesByKind.asset + snap.cfg.server.capacityBytesByKind.dictionary + snap.cfg.server.capacityBytesByKind.delta)));
    // Fill fraction
    const fillFrac = totalActual / capacity;

    // Layout area is inside padded box, sized to fill fraction (visual capacity indicator).
    const padding = 8;
    const inner = { x: padding, y: padding, w: rect.width - 2 * padding, h: rect.height - 2 * padding };
    // Draw a capacity backdrop.
    ctx.fillStyle = '#161b22';
    ctx.fillRect(inner.x, inner.y, inner.w, inner.h);
    // Fill area proportional to used bytes; the rest = empty area (dark).
    const usedH = inner.h * fillFrac;
    const layoutRect = { x: inner.x, y: inner.y + (inner.h - usedH), w: inner.w, h: usedH };
    squarify(items, layoutRect, ctx);

    // Overlay: capacity label.
    ctx.fillStyle = '#8b949e';
    ctx.font = '11px ui-monospace, monospace';
    ctx.fillText(`used ${fmt(totalActual)} / cap ${fmt(capacity)} (${(fillFrac * 100).toFixed(1)}%)`, inner.x + 4, inner.y + 12);
  }

  return { update };
}

function fmt(n) {
  if (n < 1024) return n + 'B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + 'KB';
  return (n / 1024 / 1024).toFixed(2) + 'MB';
}

// Very small squarify (approximate; splits alternately).
function squarify(items, rect, ctx) {
  const total = items.reduce((a, b) => a + b.size, 0);
  if (total <= 0 || rect.w <= 0 || rect.h <= 0) return;
  layout(items, rect, total, ctx);
}

function layout(items, rect, total, ctx) {
  if (items.length === 0) return;
  if (items.length === 1) { drawItem(items[0], rect, ctx); return; }
  const horizontal = rect.w >= rect.h;
  // Take enough items to fill roughly half the area.
  let acc = 0, splitIdx = 0;
  for (let i = 0; i < items.length; i++) {
    acc += items[i].size;
    splitIdx = i + 1;
    if (acc >= total / 2) break;
  }
  const groupA = items.slice(0, splitIdx);
  const groupB = items.slice(splitIdx);
  const fracA = acc / total;
  let rectA, rectB;
  if (horizontal) {
    rectA = { x: rect.x, y: rect.y, w: rect.w * fracA, h: rect.h };
    rectB = { x: rect.x + rect.w * fracA, y: rect.y, w: rect.w * (1 - fracA), h: rect.h };
  } else {
    rectA = { x: rect.x, y: rect.y, w: rect.w, h: rect.h * fracA };
    rectB = { x: rect.x, y: rect.y + rect.h * fracA, w: rect.w, h: rect.h * (1 - fracA) };
  }
  layout(groupA, rectA, acc, ctx);
  layout(groupB, rectB, total - acc, ctx);
}

function drawItem(it, rect, ctx) {
  const pad = 0.5;
  const x = rect.x + pad, y = rect.y + pad;
  const w = Math.max(0, rect.w - pad * 2);
  const h = Math.max(0, rect.h - pad * 2);
  if (w < 1 || h < 1) return;
  if (it.kind === 'delta') {
    const grad = ctx.createLinearGradient(x, y, x + w, y);
    grad.addColorStop(0, versionColor(it.assetId, it.fromV));
    grad.addColorStop(1, versionColor(it.assetId, it.toV));
    ctx.fillStyle = grad;
  } else {
    ctx.fillStyle = versionColor(it.assetId, it.v);
  }
  ctx.fillRect(x, y, w, h);
  if (it.kind === 'dict') {
    ctx.strokeStyle = it.pinned ? '#f0b429' : 'rgba(240,180,41,0.7)';
    ctx.lineWidth = 2;
    ctx.strokeRect(x + 1.5, y + 1.5, w - 3, h - 3);
    ctx.strokeRect(x + 3.5, y + 3.5, Math.max(0, w - 7), Math.max(0, h - 7));
  }
}
